import { useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, ErrorState, SectionCard } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const fmtComm = (c) => c.commission_mode === "FIXED" ? `₹${c.commission_value}` : c.commission_mode === "PERCENT" ? `${c.commission_value}%` : "Default rate";

function CategoryDialog({ cat, onClose, onDone }) {
  const [f, setF] = useState({ name: cat?.name || "", commission_mode: cat?.commission_mode || "PERCENT",
    commission_value: cat?.commission_value ?? "", is_active: cat?.is_active ?? true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const body = { ...f, commission_value: f.commission_value === "" ? null : Number(f.commission_value),
      commission_mode: f.commission_value === "" ? null : f.commission_mode };
    try {
      cat?.id ? await api.put(`/admin/shop-categories/${cat.id}`, body) : await api.post("/admin/shop-categories", body);
      toast.success("Category saved"); onDone(); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="shop-category-dialog">
        <DialogHeader><DialogTitle>{cat?.id ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block">Category name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="shop-category-name" /></label>
          <div>Commission mode
            <div className="flex gap-2 mt-1">{[["PERCENT", "Percentage %"], ["FIXED", "Fixed ₹ per order"]].map(([m, l]) => (
              <Button key={m} type="button" size="sm" variant={f.commission_mode === m ? "default" : "outline"} onClick={() => setF({ ...f, commission_mode: m })} data-testid={`shop-category-mode-${m.toLowerCase()}`}>{l}</Button>))}</div>
          </div>
          <label className="block">{f.commission_mode === "FIXED" ? "Amount per delivered order (₹)" : "Percentage of product subtotal after discounts, excl. GST & delivery (%)"}
            <Input type="number" min="0" step="0.01" value={f.commission_value} onChange={(e) => setF({ ...f, commission_value: e.target.value })} placeholder="Blank = use default rate" data-testid="shop-category-value" /></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="shop-category-active" />Active (shown to shopkeepers at registration)</label>
          <p className="text-xs text-muted-foreground">Changes apply to new orders only. Past and paid orders keep the commission they were placed with.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || f.name.trim().length < 2} data-testid="shop-category-save">Save commission settings</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignShop({ cats }) {
  const [q, setQ] = useState(""); const [shop, setShop] = useState(null); const [cid, setCid] = useState(""); const [reason, setReason] = useState("");
  const { data } = useFetch(q.length >= 2 ? `/admin/commission/shops?search=${encodeURIComponent(q)}&limit=10` : null, [q]);
  const go = async () => {
    try { await api.put(`/admin/shops/${shop.id}/category`, { shop_category_id: cid, reason }); toast.success("Shop category changed (audited)"); setShop(null); setReason(""); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-4 mt-6" data-testid="assign-shop-category">
      <h3 className="font-semibold font-outfit mb-2">Change a shop's category</h3>
      <div className="flex flex-wrap gap-2 items-end">
        <Input className="w-56" placeholder="Search shop name" value={q} onChange={(e) => { setQ(e.target.value); setShop(null); }} data-testid="assign-shop-search" />
        {!shop && data?.items?.map((s) => <Button key={s.id} size="sm" variant="outline" onClick={() => setShop(s)} data-testid={`assign-shop-${s.id}`}>{s.name} · {s.shop_category || "no category"}</Button>)}
        {shop && <>
          <span className="text-sm font-medium">{shop.name}</span>
          <select className="border rounded px-2 py-1.5 text-sm bg-background" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="assign-category-select">
            <option value="">Choose category</option>{cats.filter((c) => c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          <Input className="w-64" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="assign-reason" />
          <Button onClick={go} disabled={!cid || reason.trim().length < 3} data-testid="assign-save">Change category</Button></>}
      </div>
    </SectionCard>
  );
}

export default function ShopCategories() {
  const [search, setSearch] = useState(""); const [edit, setEdit] = useState(null);
  const { data, loading, error, refetch } = useFetch(`/admin/shop-categories${search ? `?search=${encodeURIComponent(search)}` : ""}`, [search]);
  const toggle = async (c) => {
    try { await api.put(`/admin/shop-categories/${c.id}`, { name: c.name, commission_mode: c.commission_mode, commission_value: c.commission_value, is_active: !c.is_active }); refetch(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Only unused categories can be deleted.`)) return;
    try { await api.delete(`/admin/shop-categories/${c.id}`); toast.success("Deleted"); refetch(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div>
      <PageHeader title="Shop Category & Commission" subtitle="Each category has its own commission: Fixed ₹ per delivered order, or % of the product subtotal after discounts (excl. GST & delivery). A shop's custom rate, if set, overrides its category." />
      <div className="flex gap-2 mb-3">
        <Input className="w-64" placeholder="Search categories" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="shop-category-search" />
        <Button className="ml-auto" onClick={() => setEdit({})} data-testid="shop-category-add">Add Category</Button>
      </div>
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <SectionCard className="p-2 overflow-x-auto">
          <table className="w-full text-sm" data-testid="shop-category-table">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-2">Category Name</th><th>Commission Mode</th><th>Commission Value</th><th>Shops</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>{data.map((c) => (
              <tr key={c.id} className="border-t" data-testid={`shop-category-row-${c.id}`}>
                <td className="p-2 font-medium">{c.name}</td>
                <td>{c.commission_mode === "FIXED" ? "Fixed ₹" : c.commission_mode === "PERCENT" ? "Percentage" : "—"}</td>
                <td className="tabular-nums">{fmtComm(c)}</td><td>{c.shop_count}</td>
                <td><span className={c.is_active ? "text-emerald-600" : "text-muted-foreground"}>{c.is_active ? "Active" : "Inactive"}</span></td>
                <td className="space-x-1 py-1">
                  <Button size="sm" variant="outline" onClick={() => setEdit(c)} data-testid={`shop-category-edit-${c.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`shop-category-toggle-${c.id}`}>{c.is_active ? "Deactivate" : "Activate"}</Button>
                  {c.shop_count === 0 && <Button size="sm" variant="ghost" onClick={() => del(c)} data-testid={`shop-category-delete-${c.id}`}>Delete</Button>}
                </td>
              </tr>))}</tbody>
          </table>
        </SectionCard>)}
      {edit && <CategoryDialog cat={edit.id ? edit : null} onClose={() => setEdit(null)} onDone={refetch} />}
      <AssignShop cats={data || []} />
    </div>
  );
}
